import { ACTIONS, MODES, PHASES } from './constants';
import { isNumeric, isRoyal, numericValue, rankToValue } from './cards';
import { computeScore } from './scoring';
import { createNewGame, dealRound } from './state';
import {
  countHandValue,
  findSubsetChoices,
  getTopCard,
  isBuildOpen,
  nextPlayerId,
  playerHasBuild,
  removeCardById,
  sameRoyal,
  shallowCloneState,
} from './utils';
import { validateMove } from './moves';
import type { Action, Card, Destination, GameState, Player, TakeAbsorbConstraints, TeamId } from './types';

export function applyAction(state: GameState | undefined, action: Action): GameState {
  if (!state) {
    if (action.type === ACTIONS.NEW_GAME) {
      return createNewGame(action.mode, action.seed, (action as any).starterPlayerId);
    }
    return createNewGame(MODES.TWO_PLAYER, 'default-seed');
  }

  switch (action.type) {
    case ACTIONS.NEW_GAME:
      return createNewGame(action.mode, action.seed, (action as any).starterPlayerId);
    case ACTIONS.DEAL_INITIAL:
      return handleStartRound(state, { type: ACTIONS.START_ROUND, roundIndex: 0 });
    case ACTIONS.START_ROUND:
      return handleStartRound(state, action);
    default:
      return resolveTurnAction(state, action);
  }
}

function resolveTurnAction(currentState: GameState, action: Action): GameState {
  const validation = validateMove(currentState, action);
  if (!validation.valid) {
    throw new Error(validation.reason);
  }

  const state = shallowCloneState(currentState);

  switch (action.type) {
    case ACTIONS.PLACE_PENDING_ROYALS:
      return handlePlacePendingRoyals(state, action.playerId);
    case ACTIONS.DROP:
      return handleDrop(state, action.playerId, action.cardId);
    case ACTIONS.TAKE_SINGLE:
      return handleTakeSingle(state, action);
    case ACTIONS.TAKE_SINGLE_ADD_TO_BUILD:
      return handleTakeSingleAddToBuild(state, action);
    case ACTIONS.TAKE_SINGLE_ADD_TO_STAY:
      return handleTakeSingleAddToStay(state, action);
    case ACTIONS.TAKE_SUBSET:
      return handleTakeSubset(state, action);
    case ACTIONS.TAKE_SUBSET_ADD_TO_BUILD:
      return handleTakeSubsetAddToBuild(state, action);
    case ACTIONS.TAKE_SUBSET_ADD_TO_STAY:
      return handleTakeSubsetAddToStay(state, action);
    case ACTIONS.TAKE_PILE:
      return handleTakePile(state, action);
    case ACTIONS.TAKE_PILE_ADD_TO_BUILD:
      return handleTakePileAddToBuild(state, action);
    case ACTIONS.TAKE_PILE_ADD_TO_STAY:
      return handleTakePileAddToStay(state, action);
    case ACTIONS.TAKE_ROYAL_FROM_COLLECTION:
      throw new Error('Taking royals directly from opponent collection is disabled.');
    case ACTIONS.REMOVE_ROYAL_COVER:
      return handleRemoveRoyalCover(state, action);
    case ACTIONS.STEAL_COLLECTION_TOP:
      return handleStealCollectionTop(state, action);
    case ACTIONS.BUILD_CREATE:
      return handleBuildCreate(state, action);
    case ACTIONS.BUILD_UPDATE:
      return handleBuildUpdate(state, action);
    case ACTIONS.ADD_TO_BUILD:
      return handleAddToBuild(state, action);
    case ACTIONS.ADD_TO_STAY:
      return handleAddToStay(state, action);
    case ACTIONS.REINFORCE_BUILD:
      return handleReinforceBuild(state, action);
    case ACTIONS.REINFORCE_STAY:
      return handleReinforceStay(state, action);
    case ACTIONS.STAY_CREATE:
      return handleStayCreate(state, action);
    case ACTIONS.STAY_FROM_OPP_COLLECTION:
      return handleStayFromOpponentCollection(state, action);
    case ACTIONS.INCREASE_OPP_TOP_TO_MATCH:
      return handleIncreaseOppTopToMatch(state, action);
    case ACTIONS.BUILD_SOLID_FROM_TABLE:
      return handleBuildSolidFromTable(state, action);
    case ACTIONS.BUILD_SOLID_FROM_OPP_TOP:
      return handleBuildSolidFromOpponentTop(state, action);
    case ACTIONS.ABSORB_CHOOSE:
      return handleAbsorbChoose(state, action);
    case ACTIONS.ABSORB_SKIP:
      return clearAbsorptionAndEndTurn(state, action.playerId);
    default:
      throw new Error(`Unsupported action ${String((action as Action).type)}`);
  }
}

function handleStartRound(state: GameState, action: Extract<Action, { type: 'START_ROUND' }>): GameState {
  if (state.phase !== PHASES.BETWEEN_ROUNDS) {
    throw new Error('Round can only start from betweenRounds phase.');
  }

  const roundIndex = action.roundIndex ?? state.meta.roundIndex;
  return dealRound(shallowCloneState(state), roundIndex);
}

function handlePlacePendingRoyals(state: GameState, playerId: string): GameState {
  const player = requirePlayer(state, playerId);
  const pending = state.royalPending[player.id];
  if (!pending.length) {
    throw new Error('No pending royals to place.');
  }

  state.collections[player.teamId].cards.push(...pending);
  state.royalPending[player.id] = [];
  pushLog(state, `${player.id} placed pending royals.`);
  return endTurn(state, playerId);
}

function isDropLegalForPlayer(state: GameState, playerId: string): boolean {
  const ownsBuild = state.builds.some((build) => build.ownerPlayerId === playerId);
  const ownsStay = state.stays.some((stay) => stay.ownerPlayerId === playerId);
  const pendingCount = state.royalPending[playerId]?.length || 0;
  return !ownsBuild && !ownsStay && pendingCount === 0;
}

function forceDropCard(state: GameState, playerId: string, cardId: string): GameState {
  const removed = removeCardById(state.hands[playerId], cardId);
  state.hands[playerId] = removed.next;
  state.tableLoose.push(removed.removed);

  state.turnContext.lastAction = {
    type: ACTIONS.DROP,
    playedCardIds: [removed.removed.id],
  };
  pushLog(state, `${playerId} dropped ${fmtCard(removed.removed)}.`);
  return endTurn(state, playerId);
}

function handleDrop(state: GameState, playerId: string, cardId: string): GameState {
  // Rule: if you try to drop a card while a matching card is on the table,
  // automatically perform the take instead (e.g., drop 3 while a 3 is on table -> take it).
  const handCard = (state.hands[playerId] || []).find((card) => card.id === cardId) || null;
  if (handCard) {
    const matchOnTable = state.tableLoose.find((card) => card.rank === handCard.rank);
    if (matchOnTable) {
      return handleTakeSingle(state, {
        type: ACTIONS.TAKE_SINGLE,
        playerId,
        cardIdFromHand: cardId,
        target: { type: 'looseCard', cardIdOnTable: matchOnTable.id },
      });
    }
  }

  const hand = state.hands[playerId];
  const removed = removeCardById(hand, cardId);
  state.hands[playerId] = removed.next;
  state.tableLoose.push(removed.removed);

  state.turnContext.lastAction = {
    type: ACTIONS.DROP,
    playedCardIds: [removed.removed.id],
  };
  pushLog(state, `${playerId} dropped ${fmtCard(removed.removed)}.`);
  return endTurn(state, playerId);
}

function handleTakeSingle(state: GameState, action: Extract<Action, { type: 'TAKE_SINGLE' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;
  const tableRemoved = removeCardById(state.tableLoose, action.target.cardIdOnTable);
  state.tableLoose = tableRemoved.next;

  const destination = state.collections[player.teamId].cards;
  destination.push(tableRemoved.removed);
  markLastCollector(state, player.id);

  const valueTarget = isNumeric(handRemoved.removed) ? numericValue(handRemoved.removed) : null;
  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_SINGLE,
    playedCardIds: [handRemoved.removed.id],
    valueTarget,
  };
  pushLog(state, `${player.id} took ${fmtCard(tableRemoved.removed)} with ${fmtCard(handRemoved.removed)}.`);

  // Royal chain capture: if you take a royal from table, and an opponent has the same royal as a top pair in collection,
  // steal that opponent royal pair and place them under your collection.
  if (isRoyal(tableRemoved.removed)) {
    Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([teamId, stack]) => {
        const cards = stack.cards;
        if (cards.length < 2) return;
        const top1 = getTopCard(cards);
        const top2 = cards[cards.length - 2];
        if (!top1 || !top2) return;
        if (!isRoyal(top1) || !isRoyal(top2)) return;
        if (top1.rank !== tableRemoved.removed.rank || top2.rank !== tableRemoved.removed.rank) return;

        const removed1 = cards.pop();
        const removed2 = cards.pop();
        if (!removed1 || !removed2) return;
        destination.unshift(removed2, removed1);
        pushLog(state, `${player.id} also took ${tableRemoved.removed.rank} royal pair from ${teamId}.`);
      });
  }

  if (valueTarget) {
    absorbIntoCollectionForTake(state, player.id, valueTarget, action.absorb);
  }

  // Rule: for every take move, the played hand card always ends up on top of the collection.
  destination.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function removeLooseCards(state: GameState, cardIds: string[]): Card[] {
  const removedCards: Card[] = [];
  let table = state.tableLoose;
  cardIds.forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    removedCards.push(removed.removed);
  });
  state.tableLoose = table;
  return removedCards;
}

function popNumericOpponentTop(state: GameState, playerTeamId: TeamId, opponentTeamId: TeamId): Card {
  if (opponentTeamId === playerTeamId) {
    throw new Error('Opponent team must differ from player team.');
  }
  const opponentStack = state.collections[opponentTeamId];
  if (!opponentStack) {
    throw new Error('Opponent collection not found.');
  }
  const top = getTopCard(opponentStack.cards);
  if (!top) {
    throw new Error('Opponent collection is empty.');
  }
  if (!isNumeric(top)) {
    throw new Error('Opponent collection top is not numeric.');
  }
  const popped = opponentStack.cards.pop();
  if (!popped || popped.id !== top.id) {
    throw new Error('Opponent top changed before resolution.');
  }
  return popped;
}

function supportIntoBuildAfterTake(
  state: GameState,
  playerId: string,
  pileId: string,
  supportTableCardIdsUsed: string[],
  includeOpponentTop?: boolean,
  opponentTeamId?: TeamId
): void {
  const player = requirePlayer(state, playerId);
  const pile = state.builds.find((entry) => entry.id === pileId);
  if (!pile) throw new Error('Build pile not found.');
  const owner = requirePlayer(state, pile.ownerPlayerId);
  if (owner.teamId !== player.teamId) throw new Error('Can only support your team build.');

  const usedCards = removeLooseCards(state, supportTableCardIdsUsed);
  if (usedCards.some((card) => !isNumeric(card))) {
    throw new Error('Build support can only use numeric table cards.');
  }

  let opponentTop: Card | null = null;
  if (includeOpponentTop) {
    if (!opponentTeamId) throw new Error('Opponent team is required when including opponent top.');
    opponentTop = popNumericOpponentTop(state, player.teamId, opponentTeamId);
  }

  const addedTotal =
    usedCards.reduce((sum, card) => sum + numericValue(card), 0) +
    (opponentTop ? numericValue(opponentTop) : 0);
  if (addedTotal !== pile.value) {
    throw new Error('Support cards must total build value.');
  }

  if (countTeamHandValue(state, player.teamId, pile.value) < 1) {
    throw new Error('Must keep one matching value card for supported build.');
  }

  pile.cards.push(...usedCards, ...(opponentTop ? [opponentTop] : []));
  pushLog(state, `${player.id} supported build ${pile.value}.`);
  absorbIntoBuild(state, pile.id, player.id);
}

function supportIntoStayAfterTake(
  state: GameState,
  playerId: string,
  pileId: string,
  supportTableCardIdsUsed: string[],
  includeOpponentTop?: boolean,
  opponentTeamId?: TeamId
): void {
  const player = requirePlayer(state, playerId);
  const pile = state.stays.find((entry) => entry.id === pileId);
  if (!pile) throw new Error('Stay pile not found.');
  const owner = requirePlayer(state, pile.ownerPlayerId);
  if (owner.teamId !== player.teamId) throw new Error('Can only support your team stay.');

  const usedCards = removeLooseCards(state, supportTableCardIdsUsed);
  if (usedCards.some((card) => !isNumeric(card))) {
    throw new Error('Stay support can only use numeric table cards.');
  }

  let opponentTop: Card | null = null;
  if (includeOpponentTop) {
    if (!opponentTeamId) throw new Error('Opponent team is required when including opponent top.');
    opponentTop = popNumericOpponentTop(state, player.teamId, opponentTeamId);
  }

  const addedTotal =
    usedCards.reduce((sum, card) => sum + numericValue(card), 0) +
    (opponentTop ? numericValue(opponentTop) : 0);
  if (addedTotal !== pile.value) {
    throw new Error('Support cards must total stay value.');
  }

  if (countHandValue(state.hands[player.id], pile.value) < 1) {
    throw new Error('Must keep one matching value card for supported stay.');
  }

  pile.cards.push(...usedCards, ...(opponentTop ? [opponentTop] : []));
  pushLog(state, `${player.id} supported stay ${pile.value}.`);
  absorbIntoStay(state, pile.id, player.id);
}

function handleTakeSingleAddToBuild(state: GameState, action: Extract<Action, { type: 'TAKE_SINGLE_ADD_TO_BUILD' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  if (action.supportTableCardIdsUsed.includes(action.takeCardIdOnTable)) {
    throw new Error('Support cards must not include take target.');
  }

  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;
  const tableRemoved = removeCardById(state.tableLoose, action.takeCardIdOnTable);
  state.tableLoose = tableRemoved.next;

  const destination = state.collections[player.teamId].cards;
  destination.push(tableRemoved.removed);
  markLastCollector(state, player.id);

  // Royal chain capture.
  if (isRoyal(tableRemoved.removed)) {
    Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([teamId, stack]) => {
        const cards = stack.cards;
        if (cards.length < 2) return;
        const top1 = getTopCard(cards);
        const top2 = cards[cards.length - 2];
        if (!top1 || !top2) return;
        if (!isRoyal(top1) || !isRoyal(top2)) return;
        if (top1.rank !== tableRemoved.removed.rank || top2.rank !== tableRemoved.removed.rank) return;

        const removed1 = cards.pop();
        const removed2 = cards.pop();
        if (!removed1 || !removed2) return;
        destination.unshift(removed2, removed1);
        pushLog(state, `${player.id} also took ${tableRemoved.removed.rank} royal pair from ${teamId}.`);
      });
  }

  supportIntoBuildAfterTake(
    state,
    player.id,
    action.supportPileId,
    action.supportTableCardIdsUsed,
    action.includeOpponentTop,
    action.opponentTeamId
  );

  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_SINGLE_ADD_TO_BUILD,
    playedCardIds: [handRemoved.removed.id],
    pileTargetId: action.supportPileId,
    valueTarget: isNumeric(handRemoved.removed) ? numericValue(handRemoved.removed) : null,
  };
  pushLog(state, `${player.id} took ${fmtCard(tableRemoved.removed)} with ${fmtCard(handRemoved.removed)} and supported build.`);

  const valueTarget = isNumeric(handRemoved.removed) ? numericValue(handRemoved.removed) : null;
  if (valueTarget) {
    absorbIntoCollectionForTake(state, player.id, valueTarget, action.absorb);
  }
  destination.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function handleTakeSingleAddToStay(state: GameState, action: Extract<Action, { type: 'TAKE_SINGLE_ADD_TO_STAY' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  if (action.supportTableCardIdsUsed.includes(action.takeCardIdOnTable)) {
    throw new Error('Support cards must not include take target.');
  }

  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;
  const tableRemoved = removeCardById(state.tableLoose, action.takeCardIdOnTable);
  state.tableLoose = tableRemoved.next;

  const destination = state.collections[player.teamId].cards;
  destination.push(tableRemoved.removed);
  markLastCollector(state, player.id);

  // Royal chain capture.
  if (isRoyal(tableRemoved.removed)) {
    Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .sort(([a], [b]) => a.localeCompare(b))
      .forEach(([teamId, stack]) => {
        const cards = stack.cards;
        if (cards.length < 2) return;
        const top1 = getTopCard(cards);
        const top2 = cards[cards.length - 2];
        if (!top1 || !top2) return;
        if (!isRoyal(top1) || !isRoyal(top2)) return;
        if (top1.rank !== tableRemoved.removed.rank || top2.rank !== tableRemoved.removed.rank) return;

        const removed1 = cards.pop();
        const removed2 = cards.pop();
        if (!removed1 || !removed2) return;
        destination.unshift(removed2, removed1);
        pushLog(state, `${player.id} also took ${tableRemoved.removed.rank} royal pair from ${teamId}.`);
      });
  }

  supportIntoStayAfterTake(
    state,
    player.id,
    action.supportPileId,
    action.supportTableCardIdsUsed,
    action.includeOpponentTop,
    action.opponentTeamId
  );

  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_SINGLE_ADD_TO_STAY,
    playedCardIds: [handRemoved.removed.id],
    pileTargetId: action.supportPileId,
    valueTarget: isNumeric(handRemoved.removed) ? numericValue(handRemoved.removed) : null,
  };
  pushLog(state, `${player.id} took ${fmtCard(tableRemoved.removed)} with ${fmtCard(handRemoved.removed)} and supported stay.`);

  const valueTarget = isNumeric(handRemoved.removed) ? numericValue(handRemoved.removed) : null;
  if (valueTarget) {
    absorbIntoCollectionForTake(state, player.id, valueTarget, action.absorb);
  }
  destination.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function handleTakeSubset(state: GameState, action: Extract<Action, { type: 'TAKE_SUBSET' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;

  if (!isNumeric(handRemoved.removed)) {
    throw new Error('Subset take requires numeric hand card.');
  }
  if (!action.target.cardIdsOnTable.length) {
    throw new Error('Subset take requires at least one table card.');
  }

  let table = state.tableLoose;
  const removedCards: Card[] = [];
  action.target.cardIdsOnTable.forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    removedCards.push(removed.removed);
  });
  state.tableLoose = table;

  if (removedCards.some((card) => !isNumeric(card))) {
    throw new Error('Subset take can only use numeric table cards.');
  }
  const subsetSum = removedCards.reduce((sum, card) => sum + numericValue(card), 0);
  const handValue = numericValue(handRemoved.removed);
  if (subsetSum !== handValue) {
    throw new Error('Subset sum must equal played hand card value.');
  }

  state.collections[player.teamId].cards.push(...removedCards);
  markLastCollector(state, player.id);

  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_SUBSET,
    playedCardIds: [handRemoved.removed.id],
    valueTarget: handValue,
  };
  pushLog(state, `${player.id} took subset (${removedCards.map(fmtCard).join(', ')}) with ${fmtCard(handRemoved.removed)}.`);

  absorbIntoCollectionForTake(state, player.id, handValue, action.absorb);
  // Rule: for every take move, the played hand card always ends up on top of the collection.
  state.collections[player.teamId].cards.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function handleTakeSubsetAddToBuild(state: GameState, action: Extract<Action, { type: 'TAKE_SUBSET_ADD_TO_BUILD' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  if (action.takeCardIdsOnTable.some((id) => action.supportTableCardIdsUsed.includes(id))) {
    throw new Error('Support cards must not include take subset cards.');
  }

  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;
  if (!isNumeric(handRemoved.removed)) {
    throw new Error('Subset take requires numeric hand card.');
  }
  if (!action.takeCardIdsOnTable.length) {
    throw new Error('Subset take requires at least one table card.');
  }

  const removedCards = removeLooseCards(state, action.takeCardIdsOnTable);
  if (removedCards.some((card) => !isNumeric(card))) {
    throw new Error('Subset take can only use numeric table cards.');
  }
  const subsetSum = removedCards.reduce((sum, card) => sum + numericValue(card), 0);
  const handValue = numericValue(handRemoved.removed);
  if (subsetSum !== handValue) {
    throw new Error('Subset sum must equal played hand card value.');
  }

  state.collections[player.teamId].cards.push(...removedCards);
  markLastCollector(state, player.id);

  supportIntoBuildAfterTake(
    state,
    player.id,
    action.supportPileId,
    action.supportTableCardIdsUsed,
    action.includeOpponentTop,
    action.opponentTeamId
  );

  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_SUBSET_ADD_TO_BUILD,
    playedCardIds: [handRemoved.removed.id],
    pileTargetId: action.supportPileId,
    valueTarget: handValue,
  };
  pushLog(state, `${player.id} took subset (${removedCards.map(fmtCard).join(', ')}) with ${fmtCard(handRemoved.removed)} and supported build.`);

  absorbIntoCollectionForTake(state, player.id, handValue, action.absorb);
  state.collections[player.teamId].cards.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function handleTakeSubsetAddToStay(state: GameState, action: Extract<Action, { type: 'TAKE_SUBSET_ADD_TO_STAY' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  if (action.takeCardIdsOnTable.some((id) => action.supportTableCardIdsUsed.includes(id))) {
    throw new Error('Support cards must not include take subset cards.');
  }

  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;
  if (!isNumeric(handRemoved.removed)) {
    throw new Error('Subset take requires numeric hand card.');
  }
  if (!action.takeCardIdsOnTable.length) {
    throw new Error('Subset take requires at least one table card.');
  }

  const removedCards = removeLooseCards(state, action.takeCardIdsOnTable);
  if (removedCards.some((card) => !isNumeric(card))) {
    throw new Error('Subset take can only use numeric table cards.');
  }
  const subsetSum = removedCards.reduce((sum, card) => sum + numericValue(card), 0);
  const handValue = numericValue(handRemoved.removed);
  if (subsetSum !== handValue) {
    throw new Error('Subset sum must equal played hand card value.');
  }

  state.collections[player.teamId].cards.push(...removedCards);
  markLastCollector(state, player.id);

  supportIntoStayAfterTake(
    state,
    player.id,
    action.supportPileId,
    action.supportTableCardIdsUsed,
    action.includeOpponentTop,
    action.opponentTeamId
  );

  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_SUBSET_ADD_TO_STAY,
    playedCardIds: [handRemoved.removed.id],
    pileTargetId: action.supportPileId,
    valueTarget: handValue,
  };
  pushLog(state, `${player.id} took subset (${removedCards.map(fmtCard).join(', ')}) with ${fmtCard(handRemoved.removed)} and supported stay.`);

  absorbIntoCollectionForTake(state, player.id, handValue, action.absorb);
  state.collections[player.teamId].cards.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function handleTakePile(state: GameState, action: Extract<Action, { type: 'TAKE_PILE' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;

  const group = action.target.type === 'build' ? state.builds : state.stays;
  const pileIndex = group.findIndex((pile) => pile.id === action.target.pileId);
  if (pileIndex < 0) throw new Error('Pile not found.');

  const [pile] = group.splice(pileIndex, 1);
  state.collections[player.teamId].cards.push(...pile.cards);
  markLastCollector(state, player.id);

  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_PILE,
    playedCardIds: [handRemoved.removed.id],
    pileTargetId: pile.id,
    valueTarget: pile.value,
  };
  pushLog(state, `${player.id} took ${pile.kind} ${pile.value}.`);

  absorbIntoCollectionForTake(state, player.id, pile.value, action.absorb);
  // Rule: for every take move, the played hand card always ends up on top of the collection.
  state.collections[player.teamId].cards.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function handleTakePileAddToBuild(state: GameState, action: Extract<Action, { type: 'TAKE_PILE_ADD_TO_BUILD' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;

  const group = action.takeTarget.type === 'build' ? state.builds : state.stays;
  const pileIndex = group.findIndex((pile) => pile.id === action.takeTarget.pileId);
  if (pileIndex < 0) throw new Error('Pile not found.');
  const [takenPile] = group.splice(pileIndex, 1);

  state.collections[player.teamId].cards.push(...takenPile.cards);
  markLastCollector(state, player.id);

  supportIntoBuildAfterTake(
    state,
    player.id,
    action.supportPileId,
    action.supportTableCardIdsUsed,
    action.includeOpponentTop,
    action.opponentTeamId
  );

  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_PILE_ADD_TO_BUILD,
    playedCardIds: [handRemoved.removed.id],
    pileTargetId: takenPile.id,
    valueTarget: takenPile.value,
  };
  pushLog(state, `${player.id} took ${takenPile.kind} ${takenPile.value} and supported build.`);

  absorbIntoCollectionForTake(state, player.id, takenPile.value, action.absorb);
  state.collections[player.teamId].cards.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function handleTakePileAddToStay(state: GameState, action: Extract<Action, { type: 'TAKE_PILE_ADD_TO_STAY' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const handRemoved = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = handRemoved.next;

  const group = action.takeTarget.type === 'build' ? state.builds : state.stays;
  const pileIndex = group.findIndex((pile) => pile.id === action.takeTarget.pileId);
  if (pileIndex < 0) throw new Error('Pile not found.');
  const [takenPile] = group.splice(pileIndex, 1);

  state.collections[player.teamId].cards.push(...takenPile.cards);
  markLastCollector(state, player.id);

  supportIntoStayAfterTake(
    state,
    player.id,
    action.supportPileId,
    action.supportTableCardIdsUsed,
    action.includeOpponentTop,
    action.opponentTeamId
  );

  state.turnContext.lastAction = {
    type: ACTIONS.TAKE_PILE_ADD_TO_STAY,
    playedCardIds: [handRemoved.removed.id],
    pileTargetId: takenPile.id,
    valueTarget: takenPile.value,
  };
  pushLog(state, `${player.id} took ${takenPile.kind} ${takenPile.value} and supported stay.`);

  absorbIntoCollectionForTake(state, player.id, takenPile.value, action.absorb);
  state.collections[player.teamId].cards.push(handRemoved.removed);
  return endTurn(state, player.id);
}

function handleRemoveRoyalCover(
  state: GameState,
  action: Extract<Action, { type: 'REMOVE_ROYAL_COVER' }>
): GameState {
  const player = requirePlayer(state, action.playerId);
  const stack = state.collections[action.targetTeamId];
  if (!stack) throw new Error('Target collection not found.');

  const top = getTopCard(stack.cards);
  if (!top || !isRoyal(top)) {
    throw new Error('Opponent collection is not covered by a royal.');
  }

  const first = removeCardById(state.hands[player.id], action.cardIdFromHand1);
  state.hands[player.id] = first.next;
  const second = removeCardById(state.hands[player.id], action.cardIdFromHand2);
  state.hands[player.id] = second.next;

  if (!sameRoyal(first.removed, second.removed)) {
    throw new Error('Presented royals must match each other.');
  }
  if (!sameRoyal(top, first.removed)) {
    throw new Error('Presented royals must match the opponent royal cover.');
  }

  const removedRoyals: Card[] = [];
  while (stack.cards.length) {
    const currentTop = getTopCard(stack.cards);
    if (!currentTop || !sameRoyal(currentTop, first.removed)) break;
    removedRoyals.push(stack.cards.pop() as Card);
  }
  state.collections[player.teamId].cards.unshift(...removedRoyals.reverse());
  state.royalPending[player.id] = [first.removed, second.removed];

  state.turnContext.lastAction = {
    type: ACTIONS.REMOVE_ROYAL_COVER,
    playedCardIds: [first.removed.id, second.removed.id],
  };
  pushLog(state, `${player.id} removed royal cover from ${action.targetTeamId}.`);
  return endTurn(state, player.id);
}

function handleStealCollectionTop(
  state: GameState,
  action: Extract<Action, { type: 'STEAL_COLLECTION_TOP' }>
): GameState {
  const hand = state.hands[action.playerId] || [];
  const first = hand.find((card) => card.id === action.cardIdFromHand1);
  const second = hand.find((card) => card.id === action.cardIdFromHand2);
  if (!first || !second || first.id === second.id || !isNumeric(first) || !isNumeric(second) || numericValue(first) !== numericValue(second)) {
    throw new Error('Steal requires two matching numeric cards in hand.');
  }
  if (numericValue(first) !== action.valueX) {
    throw new Error('Steal cards must match declared valueX.');
  }

  return handleStayFromOpponentCollection(state, {
    type: ACTIONS.STAY_FROM_OPP_COLLECTION,
    playerId: action.playerId,
    playedCardIdFromHand: action.cardIdFromHand1,
    targetTeamId: action.targetTeamId,
  });
}

function handleBuildCreate(state: GameState, action: Extract<Action, { type: 'BUILD_CREATE' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  if (playerHasBuild(state.builds, player.id)) {
    throw new Error('Player may only own one build at a time.');
  }
  if (action.buildValue < 1 || action.buildValue > 10) {
    throw new Error('Build value must be numeric from 1 to 10.');
  }
  if (state.builds.some((build) => build.value === action.buildValue)) {
    if (isDropLegalForPlayer(state, player.id)) {
      return forceDropCard(state, player.id, action.playedCardIdFromHand);
    }
    throw new Error('BUILD EXISTS');
  }
  const sameTeamBuildValue = state.builds.some((build) => {
    const owner = state.players.find((entry) => entry.id === build.ownerPlayerId);
    return owner?.teamId === player.teamId && build.value === action.buildValue;
  });
  if (sameTeamBuildValue) {
    throw new Error('Team cannot hold duplicate build value.');
  }
  const sameTeamStayValue = state.stays.some((stay) => {
    const owner = state.players.find((entry) => entry.id === stay.ownerPlayerId);
    return owner?.teamId === player.teamId && stay.value === action.buildValue;
  });
  if (sameTeamStayValue) {
    throw new Error('Cannot build a value already held as a stay by your team.');
  }

  const hand = state.hands[player.id];
  const played = removeCardById(hand, action.playedCardIdFromHand);
  state.hands[player.id] = played.next;

  if (!isNumeric(played.removed)) {
    throw new Error('Royals cannot be used in numeric builds.');
  }

  const usedCards: Card[] = [];
  let table = state.tableLoose;
  (action.tableCardIdsUsed || []).forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    usedCards.push(removed.removed);
  });
  state.tableLoose = table;

  const total = [played.removed, ...usedCards].reduce((sum, card) => {
    if (!isNumeric(card)) throw new Error('Royals cannot be used in builds.');
    return sum + numericValue(card);
  }, 0);

  if (total !== action.buildValue) {
    throw new Error('Build value must equal sum of build cards.');
  }

  const remainingMatch = countTeamHandValue(state, player.teamId, action.buildValue);
  if (remainingMatch < 1) {
    throw new Error('Must keep at least one matching value card in hand to build.');
  }

  const pileId = `build-${state.meta.nextPileId}`;
  state.meta.nextPileId += 1;
  state.builds.push({
    id: pileId,
    kind: 'build',
    cards: [played.removed, ...usedCards],
    value: action.buildValue,
    ownerPlayerId: player.id,
    isSolid: Boolean(action.isSolid),
  });

  state.turnContext.lastAction = {
    type: ACTIONS.BUILD_CREATE,
    playedCardIds: [played.removed.id],
    pileTargetId: pileId,
    valueTarget: action.buildValue,
  };
  pushLog(state, `${player.id} created build ${action.buildValue}.`);

  absorbIntoBuild(state, pileId, player.id);
  return endTurn(state, player.id);
}

function handleBuildUpdate(state: GameState, action: Extract<Action, { type: 'BUILD_UPDATE' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const pile = state.builds.find((entry) => entry.id === action.pileId);
  if (!pile) throw new Error('Build pile not found.');
  if (pile.ownerPlayerId === player.id) throw new Error('Cannot update your own build.');
  if (!isBuildOpen(pile)) throw new Error('Closed build cannot be tempered.');

  const playedCard = (state.hands[player.id] || []).find((card) => card.id === action.playedCardIdFromHand);
  if (!playedCard) throw new Error('Card not in hand.');
  if (!isNumeric(playedCard)) {
    throw new Error('Royals cannot update numeric builds.');
  }

  const referencedTableCards = (action.tableCardIdsUsed || []).map((cardId) => {
    const card = state.tableLoose.find((entry) => entry.id === cardId);
    if (!card) throw new Error(`Card ${cardId} not found.`);
    return card;
  });
  if (referencedTableCards.some((card) => !isNumeric(card))) {
    throw new Error('Build update can only use numeric table cards.');
  }

  const expectedValue =
    pile.value + numericValue(playedCard) + referencedTableCards.reduce((sum, card) => sum + numericValue(card), 0);
  const requestedValue = action.newValue ?? expectedValue;
  if (requestedValue !== expectedValue) {
    throw new Error('Updated build value must equal old value plus added card sum.');
  }
  if (requestedValue < 1 || requestedValue > 10) {
    throw new Error('Build value must be numeric from 1 to 10.');
  }
  const existingBuildWithValue = state.builds.find((build) => build.id !== pile.id && build.value === requestedValue) || null;
  if (existingBuildWithValue) {
    const existingOwner = state.players.find((entry) => entry.id === existingBuildWithValue.ownerPlayerId) || null;
    if (existingOwner?.teamId !== player.teamId) {
      if (isDropLegalForPlayer(state, player.id)) {
        return forceDropCard(state, player.id, action.playedCardIdFromHand);
      }
      throw new Error('BUILD EXISTS');
    }
  }

  const played = removeCardById(state.hands[player.id], action.playedCardIdFromHand);
  state.hands[player.id] = played.next;
  const usedCards: Card[] = [];
  let table = state.tableLoose;
  (action.tableCardIdsUsed || []).forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    usedCards.push(removed.removed);
  });
  state.tableLoose = table;
  const sameTeamStayValue = state.stays.some((stay) => {
    const owner = state.players.find((entry) => entry.id === stay.ownerPlayerId);
    return owner?.teamId === player.teamId && stay.value === requestedValue;
  });
  if (sameTeamStayValue) {
    throw new Error('Cannot build a value already held as a stay by your team.');
  }

  const remainingMatch = countTeamHandValue(state, player.teamId, requestedValue);
  if (remainingMatch < 1) {
    throw new Error('Must keep one matching value card for updated build.');
  }

  if (existingBuildWithValue) {
    existingBuildWithValue.cards.push(...pile.cards, played.removed, ...usedCards);
    state.builds = state.builds.filter((build) => build.id !== pile.id);

    state.turnContext.lastAction = {
      type: ACTIONS.BUILD_UPDATE,
      playedCardIds: [played.removed.id],
      pileTargetId: existingBuildWithValue.id,
      valueTarget: existingBuildWithValue.value,
    };
    pushLog(state, `${player.id} increased open build to ${requestedValue} and merged it into build ${requestedValue}.`);

    absorbIntoBuild(state, existingBuildWithValue.id, player.id);
    return endTurn(state, player.id);
  }

  pile.cards.push(played.removed, ...usedCards);
  pile.value = requestedValue;
  pile.ownerPlayerId = player.id;

  state.turnContext.lastAction = {
    type: ACTIONS.BUILD_UPDATE,
    playedCardIds: [played.removed.id],
    pileTargetId: pile.id,
    valueTarget: pile.value,
  };
  pushLog(state, `${player.id} increased build to ${pile.value} and became owner.`);

  absorbIntoBuild(state, pile.id, player.id);
  return endTurn(state, player.id);
}

function handleAddToBuild(state: GameState, action: Extract<Action, { type: 'ADD_TO_BUILD' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const pile = state.builds.find((entry) => entry.id === action.pileId);
  if (!pile) throw new Error('Build pile not found.');
  const owner = requirePlayer(state, pile.ownerPlayerId);
  if (owner.teamId !== player.teamId) throw new Error('Can only add to your own team build.');

  const played = removeCardById(state.hands[player.id], action.playedCardIdFromHand);
  state.hands[player.id] = played.next;
  if (!isNumeric(played.removed)) {
    throw new Error('Royals cannot update numeric builds.');
  }

  const usedCards: Card[] = [];
  let table = state.tableLoose;
  (action.tableCardIdsUsed || []).forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    usedCards.push(removed.removed);
  });
  state.tableLoose = table;

  if (usedCards.some((card) => !isNumeric(card))) {
    throw new Error('Build add can only use numeric table cards.');
  }

  let opponentTopCard: Card | null = null;
  let opponentTopValue = 0;
  if (action.includeOpponentTop) {
    if (!action.opponentTeamId) {
      throw new Error('Opponent team is required when including opponent top.');
    }
    const opponentStack = state.collections[action.opponentTeamId];
    if (!opponentStack) {
      throw new Error('Opponent collection not found.');
    }
    const top = getTopCard(opponentStack.cards);
    if (!top) {
      throw new Error('Opponent collection is empty.');
    }
    if (!isNumeric(top)) {
      throw new Error('Opponent collection top is not numeric.');
    }
    opponentTopCard = top;
    opponentTopValue = numericValue(top);
  }

  const addedTotal = [played.removed, ...usedCards].reduce((sum, card) => sum + numericValue(card), 0) + opponentTopValue;
  if (addedTotal !== pile.value) {
    throw new Error('Added cards must total build value.');
  }

  const remainingMatch = countTeamHandValue(state, player.teamId, pile.value);
  if (remainingMatch < 1) {
    throw new Error('Must keep one matching value card for updated build.');
  }

  if (action.includeOpponentTop) {
    const opponentStack = state.collections[action.opponentTeamId as string];
    const popped = opponentStack.cards.pop();
    if (!popped || !opponentTopCard || popped.id !== opponentTopCard.id) {
      throw new Error('Opponent top changed before add-to-build resolution.');
    }
    pile.cards.push(played.removed, ...usedCards, popped);
  } else {
    pile.cards.push(played.removed, ...usedCards);
  }
  state.turnContext.lastAction = {
    type: ACTIONS.ADD_TO_BUILD,
    playedCardIds: [played.removed.id],
    pileTargetId: pile.id,
    valueTarget: pile.value,
  };
  if (action.includeOpponentTop && opponentTopCard) {
    const playedValue = numericValue(played.removed);
    const looseValues = usedCards.map((card) => numericValue(card)).join(' + ');
    const parts = [String(playedValue), looseValues, `opponent top ${numericValue(opponentTopCard)}`].filter(Boolean);
    pushLog(state, `${player.id} added to build ${pile.value} using ${parts.join(' + ')}.`);
  } else {
    pushLog(state, `${player.id} added to build ${pile.id} at ${pile.value}.`);
  }
  absorbIntoBuild(state, pile.id, player.id);
  return endTurn(state, player.id);
}

function handleAddToStay(state: GameState, action: Extract<Action, { type: 'ADD_TO_STAY' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const stay = state.stays.find((entry) => entry.id === action.pileId);
  if (!stay) throw new Error('Stay pile not found.');
  const owner = requirePlayer(state, stay.ownerPlayerId);
  if (owner.teamId !== player.teamId) throw new Error('Can only add to your own team stay.');

  const played = removeCardById(state.hands[player.id], action.playedCardIdFromHand);
  state.hands[player.id] = played.next;
  if (!isNumeric(played.removed)) {
    throw new Error('Royals cannot add to stay.');
  }

  const usedCards: Card[] = [];
  let table = state.tableLoose;
  (action.tableCardIdsUsed || []).forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    usedCards.push(removed.removed);
  });
  state.tableLoose = table;

  if (usedCards.some((card) => !isNumeric(card))) {
    throw new Error('Stay add can only use numeric table cards.');
  }

  let opponentTopCard: Card | null = null;
  let opponentTopValue = 0;
  if (action.includeOpponentTop) {
    if (!action.opponentTeamId) {
      throw new Error('Opponent team is required when including opponent top.');
    }
    const opponentStack = state.collections[action.opponentTeamId];
    if (!opponentStack) {
      throw new Error('Opponent collection not found.');
    }
    const top = getTopCard(opponentStack.cards);
    if (!top) {
      throw new Error('Opponent collection is empty.');
    }
    if (!isNumeric(top)) {
      throw new Error('Opponent collection top is not numeric.');
    }
    opponentTopCard = top;
    opponentTopValue = numericValue(top);
  }

  const addedTotal = [played.removed, ...usedCards].reduce((sum, card) => sum + numericValue(card), 0) + opponentTopValue;
  if (addedTotal !== stay.value) {
    throw new Error('Added cards must total stay value.');
  }

  const remainingMatch = countHandValue(state.hands[player.id], stay.value);
  if (remainingMatch < 1) {
    throw new Error('Must keep one matching value card for stay.');
  }

  if (action.includeOpponentTop) {
    const opponentStack = state.collections[action.opponentTeamId as string];
    const popped = opponentStack.cards.pop();
    if (!popped || !opponentTopCard || popped.id !== opponentTopCard.id) {
      throw new Error('Opponent top changed before add-to-stay resolution.');
    }
    stay.cards.push(played.removed, ...usedCards, popped);
  } else {
    stay.cards.push(played.removed, ...usedCards);
  }
  state.turnContext.lastAction = {
    type: ACTIONS.ADD_TO_STAY,
    playedCardIds: [played.removed.id],
    pileTargetId: stay.id,
    valueTarget: stay.value,
  };
  if (action.includeOpponentTop && opponentTopCard) {
    const playedValue = numericValue(played.removed);
    const looseValues = usedCards.map((card) => numericValue(card)).join(' + ');
    const parts = [String(playedValue), looseValues, `opponent top ${numericValue(opponentTopCard)}`].filter(Boolean);
    pushLog(state, `${player.id} added to stay ${stay.value} using ${parts.join(' + ')}.`);
  } else {
    pushLog(state, `${player.id} added to stay ${stay.id} at ${stay.value}.`);
  }
  absorbIntoStay(state, stay.id, player.id);
  return endTurn(state, player.id);
}

function handleReinforceBuild(state: GameState, action: Extract<Action, { type: 'REINFORCE_BUILD' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const pile = state.builds.find((entry) => entry.id === action.pileId);
  if (!pile) throw new Error('Build pile not found.');
  if (pile.ownerPlayerId !== player.id) throw new Error('Can only reinforce your own build.');

  const played = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = played.next;
  if (!isNumeric(played.removed) || numericValue(played.removed) !== pile.value) {
    throw new Error('Reinforce build card must match build value.');
  }
  if (countTeamHandValue(state, player.teamId, pile.value) < 1) {
    throw new Error('Must keep one matching value card to reinforce build.');
  }

  pile.cards.push(played.removed);
  state.turnContext.lastAction = {
    type: ACTIONS.REINFORCE_BUILD,
    playedCardIds: [played.removed.id],
    pileTargetId: pile.id,
    valueTarget: pile.value,
  };
  pushLog(state, `${player.id} reinforced build ${pile.value} with ${fmtCard(played.removed)}.`);

  const wantsSelectedAbsorb = Boolean(action.absorb?.tableCardIds?.length || action.absorb?.opponentTeamIds?.length);
  if (!wantsSelectedAbsorb) {
    absorbIntoBuild(state, pile.id, player.id);
    return endTurn(state, player.id);
  }

  const oppIds = action.absorb?.opponentTeamIds || [];
  if (oppIds.length > 1) throw new Error('Reinforce-build absorb may only target one opponent team.');
  const oppTeamId = oppIds[0] || null;
  const tableCardIds = action.absorb?.tableCardIds || [];
  const selectedTableCards = tableCardIds.map((cardId) => state.tableLoose.find((card) => card.id === cardId) || null);
  if (selectedTableCards.some((card) => !card)) throw new Error('Selected table card is not available for absorb.');
  if (selectedTableCards.some((card) => card && !isNumeric(card))) throw new Error('Reinforce-build absorb can only use numeric table cards.');

  let opponentTopCard: Card | null = null;
  if (oppTeamId) {
    if (oppTeamId === player.teamId) throw new Error('Cannot absorb from your own collection.');
    const stack = state.collections[oppTeamId];
    const top = stack ? getTopCard(stack.cards) : null;
    if (!top || !isNumeric(top)) throw new Error('Selected opponent top card is not available for absorb.');
    opponentTopCard = top;
  }

  const tableSum = selectedTableCards.reduce((sum, card) => sum + numericValue(card as Card), 0);
  const opponentSum = opponentTopCard ? numericValue(opponentTopCard) : 0;
  if (tableSum + opponentSum !== pile.value) throw new Error('Selected absorb cards must sum to the build value.');

  const absorbed: Card[] = [];
  let table = state.tableLoose;
  tableCardIds.forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    absorbed.push(removed.removed);
  });
  state.tableLoose = table;

  if (oppTeamId && opponentTopCard) {
    const stack = state.collections[oppTeamId];
    const top = getTopCard(stack.cards);
    if (top && top.id === opponentTopCard.id) {
      stack.cards.pop();
      absorbed.push(opponentTopCard);
    } else {
      throw new Error('Opponent top changed before reinforce-build absorption.');
    }
  }

  pile.cards.push(...absorbed);
  state.turnContext.lastAction = {
    type: ACTIONS.REINFORCE_BUILD,
    playedCardIds: [played.removed.id, ...absorbed.map((card) => card.id)],
    pileTargetId: pile.id,
    valueTarget: pile.value,
  };
  pushLog(state, `${player.id} absorbed into build ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);

  return endTurn(state, player.id);
}

function handleReinforceStay(state: GameState, action: Extract<Action, { type: 'REINFORCE_STAY' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const pile = state.stays.find((entry) => entry.id === action.pileId);
  if (!pile) throw new Error('Stay pile not found.');
  if (pile.ownerPlayerId !== player.id) throw new Error('Can only reinforce your own stay.');

  const played = removeCardById(state.hands[player.id], action.cardIdFromHand);
  state.hands[player.id] = played.next;
  if (!isNumeric(played.removed) || numericValue(played.removed) !== pile.value) {
    throw new Error('Reinforce stay card must match stay value.');
  }
  if (countHandValue(state.hands[player.id], pile.value) < 1) {
    throw new Error('Must keep one matching value card to reinforce stay.');
  }

  pile.cards.push(played.removed);
  state.turnContext.lastAction = {
    type: ACTIONS.REINFORCE_STAY,
    playedCardIds: [played.removed.id],
    pileTargetId: pile.id,
    valueTarget: pile.value,
  };
  pushLog(state, `${player.id} reinforced stay ${pile.value} with ${fmtCard(played.removed)}.`);

  const wantsSelectedAbsorb = Boolean(action.absorb?.tableCardIds?.length || action.absorb?.opponentTeamIds?.length);
  if (!wantsSelectedAbsorb) {
    absorbIntoStay(state, pile.id, player.id);
    return endTurn(state, player.id);
  }

  const oppIds = action.absorb?.opponentTeamIds || [];
  if (oppIds.length > 1) throw new Error('Reinforce-stay absorb may only target one opponent team.');
  const oppTeamId = oppIds[0] || null;
  const tableCardIds = action.absorb?.tableCardIds || [];
  const selectedTableCards = tableCardIds.map((cardId) => state.tableLoose.find((card) => card.id === cardId) || null);
  if (selectedTableCards.some((card) => !card)) throw new Error('Selected table card is not available for absorb.');
  if (selectedTableCards.some((card) => card && !isNumeric(card))) throw new Error('Reinforce-stay absorb can only use numeric table cards.');

  let opponentTopCard: Card | null = null;
  if (oppTeamId) {
    if (oppTeamId === player.teamId) throw new Error('Cannot absorb from your own collection.');
    const stack = state.collections[oppTeamId];
    const top = stack ? getTopCard(stack.cards) : null;
    if (!top || !isNumeric(top)) throw new Error('Selected opponent top card is not available for absorb.');
    opponentTopCard = top;
  }

  const tableSum = selectedTableCards.reduce((sum, card) => sum + numericValue(card as Card), 0);
  const opponentSum = opponentTopCard ? numericValue(opponentTopCard) : 0;
  if (tableSum + opponentSum !== pile.value) throw new Error('Selected absorb cards must sum to the stay value.');

  const absorbed: Card[] = [];
  let table = state.tableLoose;
  tableCardIds.forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    absorbed.push(removed.removed);
  });
  state.tableLoose = table;

  if (oppTeamId && opponentTopCard) {
    const stack = state.collections[oppTeamId];
    const top = getTopCard(stack.cards);
    if (top && top.id === opponentTopCard.id) {
      stack.cards.pop();
      absorbed.push(opponentTopCard);
    } else {
      throw new Error('Opponent top changed before reinforce-stay absorption.');
    }
  }

  pile.cards.push(...absorbed);
  state.turnContext.lastAction = {
    type: ACTIONS.REINFORCE_STAY,
    playedCardIds: [played.removed.id, ...absorbed.map((card) => card.id)],
    pileTargetId: pile.id,
    valueTarget: pile.value,
  };
  pushLog(state, `${player.id} absorbed into stay ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);

  return endTurn(state, player.id);
}

function handleStayCreate(state: GameState, action: Extract<Action, { type: 'STAY_CREATE' }>): GameState {
  const player = requirePlayer(state, action.playerId);
  const playedCard = (state.hands[player.id] || []).find((card) => card.id === action.playedCardIdFromHand);
  if (!playedCard) throw new Error('Card not in hand.');
  if (!isNumeric(playedCard)) {
    throw new Error('Stay requires numeric cards.');
  }

  const tableCard = state.tableLoose.find((card) => card.id === action.matchingTableCardId);
  if (!tableCard) throw new Error('Table card not found.');
  if (!isNumeric(tableCard)) throw new Error('Stay requires numeric table card.');
  if (numericValue(playedCard) !== numericValue(tableCard)) {
    throw new Error('Stay cards must match numeric value.');
  }

  const value = numericValue(playedCard);
  const existingStayWithValue = state.stays.find((stay) => stay.value === value) || null;
  if (existingStayWithValue) {
    const existingOwner = state.players.find((entry) => entry.id === existingStayWithValue.ownerPlayerId) || null;
    if (existingOwner?.teamId !== player.teamId) {
      if (isDropLegalForPlayer(state, player.id)) {
        return forceDropCard(state, player.id, action.playedCardIdFromHand);
      }
      throw new Error('STAY EXISTS');
    }
  }

  const played = removeCardById(state.hands[player.id], action.playedCardIdFromHand);
  state.hands[player.id] = played.next;
  const tableRemoved = removeCardById(state.tableLoose, action.matchingTableCardId);
  state.tableLoose = tableRemoved.next;
  const sameTeamBuildValue = state.builds.some((build) => {
    const owner = state.players.find((entry) => entry.id === build.ownerPlayerId);
    return owner?.teamId === player.teamId && build.value === value;
  });
  if (sameTeamBuildValue) {
    throw new Error('Cannot create a stay with a value already held as a build by your team.');
  }
  const remaining = countHandValue(state.hands[player.id], value);
  if (remaining < 1) {
    throw new Error('Need another same-value card in hand to create stay.');
  }

  const sameTeamPlayerIds = state.players.filter((entry) => entry.teamId === player.teamId).map((entry) => entry.id);
  const existing = state.stays.find((stay) => stay.value === value && sameTeamPlayerIds.includes(stay.ownerPlayerId));

  if (existing) {
    existing.cards.push(played.removed, tableRemoved.removed);
  } else {
    const pileId = `stay-${state.meta.nextPileId}`;
    state.meta.nextPileId += 1;
    state.stays.push({
      id: pileId,
      kind: 'stay',
      value,
      ownerPlayerId: player.id,
      cards: [played.removed, tableRemoved.removed],
    });
  }

  state.turnContext.lastAction = {
    type: ACTIONS.STAY_CREATE,
    playedCardIds: [played.removed.id],
    valueTarget: value,
  };
  pushLog(state, `${player.id} created/extended stay ${value}.`);

  const sameTeamPlayerIdsAfter = state.players.filter((entry) => entry.teamId === player.teamId).map((entry) => entry.id);
  const targetStay = state.stays.find((entry) => entry.value === value && sameTeamPlayerIdsAfter.includes(entry.ownerPlayerId));
  if (!targetStay) throw new Error('Stay destination not found.');
  absorbIntoStay(state, targetStay.id, player.id);
  return endTurn(state, player.id);
}

function handleStayFromOpponentCollection(
  state: GameState,
  action: Extract<Action, { type: 'STAY_FROM_OPP_COLLECTION' }>
): GameState {
  const player = requirePlayer(state, action.playerId);
  const playedCard = (state.hands[player.id] || []).find((card) => card.id === action.playedCardIdFromHand);
  if (!playedCard) throw new Error('Card not in hand.');
  if (!isNumeric(playedCard)) {
    throw new Error('Stay from collection requires numeric card.');
  }
  const value = numericValue(playedCard);
  const existingStayWithValue = state.stays.find((stay) => stay.value === value) || null;
  if (existingStayWithValue) {
    const existingOwner = state.players.find((entry) => entry.id === existingStayWithValue.ownerPlayerId) || null;
    if (existingOwner?.teamId !== player.teamId) {
      if (isDropLegalForPlayer(state, player.id)) {
        return forceDropCard(state, player.id, action.playedCardIdFromHand);
      }
      throw new Error('STAY EXISTS');
    }
  }

  const played = removeCardById(state.hands[player.id], action.playedCardIdFromHand);
  state.hands[player.id] = played.next;
  const sameTeamBuildValue = state.builds.some((build) => {
    const owner = state.players.find((entry) => entry.id === build.ownerPlayerId);
    return owner?.teamId === player.teamId && build.value === value;
  });
  if (sameTeamBuildValue) {
    throw new Error('Cannot create a stay with a value already held as a build by your team.');
  }
  const remaining = countHandValue(state.hands[player.id], value);
  if (remaining < 1) {
    throw new Error('Need another same-value card in hand after stay-from-collection.');
  }

  const targetStack = state.collections[action.targetTeamId];
  const top = getTopCard(targetStack.cards);
  if (!top || !isNumeric(top)) {
    throw new Error('Opponent collection top is not numeric.');
  }

  if (numericValue(top) !== value) {
    throw new Error('Collection top must match played value for stay.');
  }

  const removedFromOpponent: Card[] = [];
  while (targetStack.cards.length) {
    const currentTop = getTopCard(targetStack.cards);
    if (!currentTop || !isNumeric(currentTop) || numericValue(currentTop) !== value) break;
    removedFromOpponent.push(targetStack.cards.pop() as Card);
  }

  const sameTeamPlayerIds = state.players.filter((entry) => entry.teamId === player.teamId).map((entry) => entry.id);
  const existing = state.stays.find((stay) => stay.value === value && sameTeamPlayerIds.includes(stay.ownerPlayerId));
  let pileId: string;
  if (existing) {
    existing.cards.push(played.removed, ...removedFromOpponent);
    pileId = existing.id;
  } else {
    pileId = `stay-${state.meta.nextPileId}`;
    state.meta.nextPileId += 1;
    state.stays.push({
      id: pileId,
      kind: 'stay',
      value,
      ownerPlayerId: player.id,
      cards: [played.removed, ...removedFromOpponent],
    });
  }

  state.turnContext.lastAction = {
    type: ACTIONS.STAY_FROM_OPP_COLLECTION,
    playedCardIds: [played.removed.id],
    valueTarget: value,
  };
  pushLog(state, `${player.id} created stay ${value} from opponent collection.`);

  absorbIntoStay(state, pileId, player.id);
  return endTurn(state, player.id);
}

function handleIncreaseOppTopToMatch(
  state: GameState,
  action: Extract<Action, { type: 'INCREASE_OPP_TOP_TO_MATCH' }>
): GameState {
  const player = requirePlayer(state, action.playerId);
  const played = removeCardById(state.hands[player.id], action.playedCardIdFromHand);

  if (!isNumeric(played.removed)) {
    throw new Error('Increase opponent top requires numeric hand card.');
  }

  const targetStack = state.collections[action.targetTeamId];
  if (!targetStack) {
    throw new Error('Target collection not found.');
  }
  const top = getTopCard(targetStack.cards);
  if (!top || !isNumeric(top)) {
    throw new Error('Opponent collection top is not numeric.');
  }

  const usedCards = (action.tableCardIdsUsed || []).map((cardId) => {
    const card = state.tableLoose.find((entry) => entry.id === cardId);
    if (!card) throw new Error(`Card ${cardId} not found.`);
    return card;
  });
  if (usedCards.some((card) => !isNumeric(card))) {
    throw new Error('Increase opponent top can only use numeric table cards.');
  }

  const addedTotal = [played.removed, ...usedCards].reduce((sum, card) => sum + numericValue(card), 0);
  const v2 = numericValue(top) + addedTotal;
  if (state.builds.some((build) => build.value === v2)) {
    if (isDropLegalForPlayer(state, player.id)) {
      return forceDropCard(state, player.id, action.playedCardIdFromHand);
    }
    throw new Error('BUILD EXISTS');
  }
  const sameTeamStayValue = state.stays.some((stay) => {
    const owner = state.players.find((entry) => entry.id === stay.ownerPlayerId);
    return owner?.teamId === player.teamId && stay.value === v2;
  });
  if (sameTeamStayValue) {
    throw new Error('Cannot build a value already held as a stay by your team.');
  }
  const anchor = state.tableLoose.find((entry) => entry.id === action.anchorLooseCardId);
  if (!anchor || !isNumeric(anchor) || numericValue(anchor) !== v2) {
    throw new Error('Anchor loose card for target value is required.');
  }

  // Commitment rule: if you create a closed build value v2, you must hold a v2 card in your (team) hand
  // so you can take the build later. Otherwise drop is forbidden while owning a build and gameplay can stall.
  if (countTeamHandValue(state, player.teamId, v2) < 1) {
    throw new Error(`Cannot create closed build ${v2} without holding a ${v2} card to take it out later.`);
  }

  state.hands[player.id] = played.next;

  let table = state.tableLoose;
  const removedLoose: Card[] = [];
  (action.tableCardIdsUsed || []).forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    removedLoose.push(removed.removed);
  });
  const removedAnchor = removeCardById(table, action.anchorLooseCardId);
  table = removedAnchor.next;
  state.tableLoose = table;

  const removedTop = targetStack.cards.pop();
  if (!removedTop) throw new Error('Opponent collection is empty.');

  const pileId = `build-${state.meta.nextPileId}`;
  state.meta.nextPileId += 1;
  state.builds.push({
    id: pileId,
    kind: 'build',
    cards: [removedTop, played.removed, ...removedLoose, removedAnchor.removed],
    value: v2,
    ownerPlayerId: player.id,
    isSolid: true,
  });

  state.turnContext.lastAction = {
    type: ACTIONS.INCREASE_OPP_TOP_TO_MATCH,
    playedCardIds: [played.removed.id],
    pileTargetId: pileId,
    valueTarget: v2,
  };
  pushLog(state, `${player.id} increased opponent top into closed build ${v2}.`);
  return endTurn(state, player.id);
}

function handleBuildSolidFromOpponentTop(
  state: GameState,
  action: Extract<Action, { type: 'BUILD_SOLID_FROM_OPP_TOP' }>
): GameState {
  const player = requirePlayer(state, action.playerId);
  if (playerHasBuild(state.builds, player.id)) {
    throw new Error('Player may only own one build at a time.');
  }

  const playedCard = (state.hands[player.id] || []).find((card) => card.id === action.playedCardIdFromHand);
  if (!playedCard) throw new Error('Card not in hand.');
  if (!isNumeric(playedCard)) {
    throw new Error('Build requires numeric cards.');
  }

  const buildValue = numericValue(playedCard);
  if (buildValue < 1 || buildValue > 10) {
    throw new Error('Build value must be numeric from 1 to 10.');
  }
  if (state.builds.some((build) => build.value === buildValue)) {
    if (isDropLegalForPlayer(state, player.id)) {
      return forceDropCard(state, player.id, action.playedCardIdFromHand);
    }
    throw new Error('BUILD EXISTS');
  }
  const sameTeamStayValue = state.stays.some((stay) => {
    const owner = state.players.find((entry) => entry.id === stay.ownerPlayerId);
    return owner?.teamId === player.teamId && stay.value === buildValue;
  });
  if (sameTeamStayValue) {
    throw new Error('Cannot build a value already held as a stay by your team.');
  }
  const played = removeCardById(state.hands[player.id], action.playedCardIdFromHand);
  state.hands[player.id] = played.next;

  const targetStack = state.collections[action.targetTeamId];
  if (!targetStack) {
    throw new Error('Target collection not found.');
  }
  const top = getTopCard(targetStack.cards);
  if (!top || !isNumeric(top)) {
    throw new Error('Opponent collection top is not numeric.');
  }
  const opponentTopValue = numericValue(top);

  const usedCards: Card[] = [];
  let table = state.tableLoose;
  (action.tableCardIdsUsed || []).forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    usedCards.push(removed.removed);
  });
  state.tableLoose = table;

  if (usedCards.length === 0) {
    throw new Error('Must use at least one table card to build from opponent top.');
  }
  if (usedCards.some((card) => !isNumeric(card))) {
    throw new Error('Build from opponent top can only use numeric table cards.');
  }

  const tableSum = usedCards.reduce((sum, card) => sum + numericValue(card), 0);
  if (opponentTopValue + tableSum !== buildValue) {
    throw new Error('Opponent top plus table cards must total the build value.');
  }

  const remainingMatch = countTeamHandValue(state, player.teamId, buildValue);
  if (remainingMatch < 1) {
    throw new Error('Must keep one matching value card in hand to build.');
  }

  const removedTop = targetStack.cards.pop();
  if (!removedTop || removedTop.id !== top.id) {
    throw new Error('Opponent top changed before build resolution.');
  }

  const pileId = `build-${state.meta.nextPileId}`;
  state.meta.nextPileId += 1;
  state.builds.push({
    id: pileId,
    kind: 'build',
    cards: [removedTop, ...usedCards, played.removed],
    value: buildValue,
    ownerPlayerId: player.id,
    isSolid: true,
  });

  state.turnContext.lastAction = {
    type: ACTIONS.BUILD_SOLID_FROM_OPP_TOP,
    playedCardIds: [played.removed.id],
    pileTargetId: pileId,
    valueTarget: buildValue,
  };
  pushLog(state, `${player.id} built closed build ${buildValue} from opponent top.`);
  return endTurn(state, player.id);
}

function handleBuildSolidFromTable(
  state: GameState,
  action: Extract<Action, { type: 'BUILD_SOLID_FROM_TABLE' }>
): GameState {
  const player = requirePlayer(state, action.playerId);
  if (playerHasBuild(state.builds, player.id)) {
    throw new Error('Player may only own one build at a time.');
  }

  const playedCard = (state.hands[player.id] || []).find((card) => card.id === action.playedCardIdFromHand);
  if (!playedCard) throw new Error('Card not in hand.');
  if (!isNumeric(playedCard)) {
    throw new Error('Build requires numeric cards.');
  }

  const buildValue = numericValue(playedCard);
  if (buildValue < 1 || buildValue > 10) {
    throw new Error('Build value must be numeric from 1 to 10.');
  }
  if (state.builds.some((build) => build.value === buildValue)) {
    if (isDropLegalForPlayer(state, player.id)) {
      return forceDropCard(state, player.id, action.playedCardIdFromHand);
    }
    throw new Error('BUILD EXISTS');
  }
  const sameTeamStayValue = state.stays.some((stay) => {
    const owner = state.players.find((entry) => entry.id === stay.ownerPlayerId);
    return owner?.teamId === player.teamId && stay.value === buildValue;
  });
  if (sameTeamStayValue) {
    throw new Error('Cannot build a value already held as a stay by your team.');
  }
  const played = removeCardById(state.hands[player.id], action.playedCardIdFromHand);
  state.hands[player.id] = played.next;

  const usedCards: Card[] = [];
  let table = state.tableLoose;
  (action.tableCardIdsUsed || []).forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    usedCards.push(removed.removed);
  });
  state.tableLoose = table;

  if (usedCards.length === 0) {
    throw new Error('Must use at least one table card to build from table.');
  }
  if (usedCards.some((card) => !isNumeric(card))) {
    throw new Error('Build from table can only use numeric table cards.');
  }
  const tableSum = usedCards.reduce((sum, card) => sum + numericValue(card), 0);
  if (tableSum !== buildValue) {
    throw new Error('Table cards must total the build value.');
  }

  const remainingMatch = countTeamHandValue(state, player.teamId, buildValue);
  if (remainingMatch < 1) {
    throw new Error('Must keep one matching value card in hand to build.');
  }

  const pileId = `build-${state.meta.nextPileId}`;
  state.meta.nextPileId += 1;
  state.builds.push({
    id: pileId,
    kind: 'build',
    cards: [...usedCards, played.removed],
    value: buildValue,
    ownerPlayerId: player.id,
    isSolid: true,
  });

  state.turnContext.lastAction = {
    type: ACTIONS.BUILD_SOLID_FROM_TABLE,
    playedCardIds: [played.removed.id],
    pileTargetId: pileId,
    valueTarget: buildValue,
  };
  pushLog(state, `${player.id} built closed build ${buildValue} from table.`);
  return endTurn(state, player.id);
}

function handleAbsorbChoose(state: GameState, action: Extract<Action, { type: 'ABSORB_CHOOSE' }>): GameState {
  const pending = state.turnContext.pendingAbsorption;
  if (!pending) {
    throw new Error('No pending absorption choices.');
  }

  const choice = pending.choices.find((entry) => entry.id === action.choiceId);
  if (!choice) throw new Error('Absorption choice not found.');

  const absorbed: Card[] = [];
  let table = state.tableLoose;
  choice.tableCardIds.forEach((cardId) => {
    const removed = removeCardById(table, cardId);
    table = removed.next;
    absorbed.push(removed.removed);
  });
  state.tableLoose = table;

  if (choice.collectionTopTeamId) {
    const stack = state.collections[choice.collectionTopTeamId];
    const top = getTopCard(stack.cards);
    if (top && isNumeric(top) && numericValue(top) === pending.value) {
      stack.cards.pop();
      absorbed.push(top);
    }
  }

  const destination = resolveDestination(state, pending.destination);
  destination.push(...absorbed);
  if (absorbed.length > 0 && pending.destination.zone === 'collection') {
    markLastCollector(state, action.playerId);
  }
  if (absorbed.length > 0) {
    pushLog(state, `${action.playerId} absorbed ${absorbed.map(fmtCard).join(', ')}.`);
  }

  return refreshAbsorptionOrEnd(state, action.playerId);
}

function clearAbsorptionAndEndTurn(state: GameState, playerId: string): GameState {
  state.turnContext.pendingAbsorption = null;
  return endTurn(state, playerId);
}

function absorbIntoBuild(state: GameState, buildPileId: string, playerId: string): void {
  const pile = state.builds.find((entry) => entry.id === buildPileId);
  if (!pile) throw new Error('Build pile not found.');
  const player = requirePlayer(state, playerId);
  const targetValue = pile.value;
  if (targetValue < 1 || targetValue > 10) return;

  let changed = true;
  while (changed) {
    changed = false;

    const singleMatches = state.tableLoose.filter((card) => isNumeric(card) && numericValue(card) === targetValue);
    if (singleMatches.length > 0) {
      const absorbed: Card[] = [];
      let table = state.tableLoose;
      singleMatches.forEach((card) => {
        const removed = removeCardById(table, card.id);
        table = removed.next;
        absorbed.push(removed.removed);
      });
      state.tableLoose = table;
      pile.cards.push(...absorbed);
      pushLog(state, `${playerId} auto-absorbed into build ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);
      changed = true;
      continue;
    }

    const topSingles = Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .map(([teamId, stack]) => ({ teamId, stack, top: getTopCard(stack.cards) }))
      .filter(({ top }) => Boolean(top && isNumeric(top) && numericValue(top) === targetValue))
      .sort((a, b) => a.teamId.localeCompare(b.teamId));
    if (topSingles.length > 0) {
      const absorbed: Card[] = [];
      topSingles.forEach(({ stack }) => {
        const top = getTopCard(stack.cards);
        if (!top || !isNumeric(top) || numericValue(top) !== targetValue) return;
        stack.cards.pop();
        absorbed.push(top);
      });
      if (absorbed.length > 0) {
        pile.cards.push(...absorbed);
        pushLog(state, `${playerId} auto-absorbed into build ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);
        changed = true;
        continue;
      }
    }

    const tableSubset = findSubsetChoices(state.tableLoose, targetValue).find((choice) => choice.length > 1);
    const comboCandidates = Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .map(([teamId, stack]) => {
        const top = getTopCard(stack.cards);
        if (!top || !isNumeric(top)) return null;
        const neededFromTable = targetValue - numericValue(top);
        if (neededFromTable <= 0) return null;
        const subset = findSubsetChoices(state.tableLoose, neededFromTable)[0];
        if (!subset || subset.length < 1) return null;
        return { teamId, topCardId: top.id, subset };
      })
      .filter((entry): entry is { teamId: string; topCardId: string; subset: Card[] } => Boolean(entry))
      .sort((a, b) => {
        const countDiff = a.subset.length - b.subset.length;
        if (countDiff !== 0) return countDiff;
        const aSig = `${a.topCardId}|${a.subset.map((card) => card.id).sort().join('|')}`;
        const bSig = `${b.topCardId}|${b.subset.map((card) => card.id).sort().join('|')}`;
        return aSig.localeCompare(bSig);
      });
    const topPlusTable = comboCandidates[0];

    if (tableSubset && (!topPlusTable || tableSubset.length <= topPlusTable.subset.length + 1)) {
      const absorbed: Card[] = [];
      let table = state.tableLoose;
      tableSubset.forEach((card) => {
        const removed = removeCardById(table, card.id);
        table = removed.next;
        absorbed.push(removed.removed);
      });
      state.tableLoose = table;
      pile.cards.push(...absorbed);
      pushLog(state, `${playerId} auto-absorbed into build ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);
      changed = true;
      continue;
    }

    if (topPlusTable) {
      const absorbed: Card[] = [];
      let table = state.tableLoose;
      topPlusTable.subset.forEach((card) => {
        const removed = removeCardById(table, card.id);
        table = removed.next;
        absorbed.push(removed.removed);
      });
      state.tableLoose = table;

      const stack = state.collections[topPlusTable.teamId];
      const top = getTopCard(stack.cards);
      if (top && top.id === topPlusTable.topCardId && isNumeric(top)) {
        stack.cards.pop();
        absorbed.push(top);
      }

      if (absorbed.length > 0) {
        pile.cards.push(...absorbed);
        pushLog(state, `${playerId} auto-absorbed into build ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);
        changed = true;
      }
    }
  }
}

function absorbIntoStay(state: GameState, stayPileId: string, playerId: string): void {
  const pile = state.stays.find((entry) => entry.id === stayPileId);
  if (!pile) throw new Error('Stay pile not found.');
  const player = requirePlayer(state, playerId);
  const targetValue = pile.value;
  if (targetValue < 1 || targetValue > 10) return;

  let changed = true;
  while (changed) {
    changed = false;

    const singleMatches = state.tableLoose.filter((card) => isNumeric(card) && numericValue(card) === targetValue);
    if (singleMatches.length > 0) {
      const absorbed: Card[] = [];
      let table = state.tableLoose;
      singleMatches.forEach((card) => {
        const removed = removeCardById(table, card.id);
        table = removed.next;
        absorbed.push(removed.removed);
      });
      state.tableLoose = table;
      pile.cards.push(...absorbed);
      pushLog(state, `${playerId} auto-absorbed into stay ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);
      changed = true;
      continue;
    }

    const topSingles = Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .map(([teamId, stack]) => ({ teamId, stack, top: getTopCard(stack.cards) }))
      .filter(({ top }) => Boolean(top && isNumeric(top) && numericValue(top) === targetValue))
      .sort((a, b) => a.teamId.localeCompare(b.teamId));
    if (topSingles.length > 0) {
      const absorbed: Card[] = [];
      topSingles.forEach(({ stack }) => {
        const top = getTopCard(stack.cards);
        if (!top || !isNumeric(top) || numericValue(top) !== targetValue) return;
        stack.cards.pop();
        absorbed.push(top);
      });
      if (absorbed.length > 0) {
        pile.cards.push(...absorbed);
        pushLog(state, `${playerId} auto-absorbed into stay ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);
        changed = true;
        continue;
      }
    }

    const tableSubset = findSubsetChoices(state.tableLoose, targetValue).find((choice) => choice.length > 1);
    const comboCandidates = Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .map(([teamId, stack]) => {
        const top = getTopCard(stack.cards);
        if (!top || !isNumeric(top)) return null;
        const neededFromTable = targetValue - numericValue(top);
        if (neededFromTable <= 0) return null;
        const subset = findSubsetChoices(state.tableLoose, neededFromTable)[0];
        if (!subset || subset.length < 1) return null;
        return { teamId, topCardId: top.id, subset };
      })
      .filter((entry): entry is { teamId: string; topCardId: string; subset: Card[] } => Boolean(entry))
      .sort((a, b) => {
        const countDiff = a.subset.length - b.subset.length;
        if (countDiff !== 0) return countDiff;
        const aSig = `${a.topCardId}|${a.subset.map((card) => card.id).sort().join('|')}`;
        const bSig = `${b.topCardId}|${b.subset.map((card) => card.id).sort().join('|')}`;
        return aSig.localeCompare(bSig);
      });
    const topPlusTable = comboCandidates[0];

    if (tableSubset && (!topPlusTable || tableSubset.length <= topPlusTable.subset.length + 1)) {
      const absorbed: Card[] = [];
      let table = state.tableLoose;
      tableSubset.forEach((card) => {
        const removed = removeCardById(table, card.id);
        table = removed.next;
        absorbed.push(removed.removed);
      });
      state.tableLoose = table;
      pile.cards.push(...absorbed);
      pushLog(state, `${playerId} auto-absorbed into stay ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);
      changed = true;
      continue;
    }

    if (topPlusTable) {
      const absorbed: Card[] = [];
      let table = state.tableLoose;
      topPlusTable.subset.forEach((card) => {
        const removed = removeCardById(table, card.id);
        table = removed.next;
        absorbed.push(removed.removed);
      });
      state.tableLoose = table;

      const stack = state.collections[topPlusTable.teamId];
      const top = getTopCard(stack.cards);
      if (top && top.id === topPlusTable.topCardId && isNumeric(top)) {
        stack.cards.pop();
        absorbed.push(top);
      }

      if (absorbed.length > 0) {
        pile.cards.push(...absorbed);
        pushLog(state, `${playerId} auto-absorbed into stay ${pile.value}: ${absorbed.map(fmtCard).join(', ')}.`);
        changed = true;
      }
    }
  }
}

function normalizeAbsorbConstraints(absorb: TakeAbsorbConstraints | undefined): {
  tableCardIds: Set<string> | null;
  opponentTeamIds: Set<TeamId> | null;
} {
  const tableCardIds = absorb?.tableCardIds ? new Set(absorb.tableCardIds) : null;
  const opponentTeamIds = absorb?.opponentTeamIds ? new Set(absorb.opponentTeamIds) : null;
  return { tableCardIds, opponentTeamIds };
}

function absorbIntoCollectionForTake(
  state: GameState,
  playerId: string,
  targetValue: number,
  absorb?: TakeAbsorbConstraints
): void {
  const player = requirePlayer(state, playerId);
  const destination = state.collections[player.teamId].cards;
  if (targetValue < 1 || targetValue > 10) return;
  const constraints = normalizeAbsorbConstraints(absorb);
  const tableAllowed = (cardId: string): boolean => !constraints.tableCardIds || constraints.tableCardIds.has(cardId);
  const teamAllowed = (teamId: TeamId): boolean => !constraints.opponentTeamIds || constraints.opponentTeamIds.has(teamId);

  let changed = true;
  while (changed) {
    changed = false;

    const exactTop = Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .filter(([teamId]) => teamAllowed(teamId))
      .map(([teamId, stack]) => ({ teamId, stack, top: getTopCard(stack.cards) }))
      .filter(({ top }) => Boolean(top && isNumeric(top) && numericValue(top) === targetValue))
      .sort((a, b) => a.teamId.localeCompare(b.teamId))[0];
    if (exactTop) {
      const top = exactTop.stack.cards.pop();
      if (top) {
        destination.push(top);
        pushLog(state, `${playerId} auto-absorbed: ${fmtCard(top)}.`);
        changed = true;
        continue;
      }
    }

    const exactTable = state.tableLoose.find((card) => isNumeric(card) && numericValue(card) === targetValue && tableAllowed(card.id));
    if (exactTable) {
      const removed = removeCardById(state.tableLoose, exactTable.id);
      state.tableLoose = removed.next;
      destination.push(removed.removed);
      pushLog(state, `${playerId} auto-absorbed: ${fmtCard(removed.removed)}.`);
      changed = true;
      continue;
    }

    const topPlusSubset = Object.entries(state.collections)
      .filter(([teamId]) => teamId !== player.teamId)
      .filter(([teamId]) => teamAllowed(teamId))
      .map(([teamId, stack]) => {
        const top = getTopCard(stack.cards);
        if (!top || !isNumeric(top)) return null;
        const needed = targetValue - numericValue(top);
        if (needed <= 0) return null;
        const subset = findSubsetChoices(state.tableLoose.filter((card) => tableAllowed(card.id)), needed)[0];
        if (!subset || subset.length < 1) return null;
        return { teamId, topCardId: top.id, subset };
      })
      .filter((entry): entry is { teamId: string; topCardId: string; subset: Card[] } => Boolean(entry))
      .sort((a, b) => {
        const lenDiff = a.subset.length - b.subset.length;
        if (lenDiff !== 0) return lenDiff;
        const aSig = `${a.teamId}|${a.topCardId}|${a.subset.map((card) => card.id).sort().join('|')}`;
        const bSig = `${b.teamId}|${b.topCardId}|${b.subset.map((card) => card.id).sort().join('|')}`;
        return aSig.localeCompare(bSig);
      })[0];

    const tableSubset = findSubsetChoices(state.tableLoose.filter((card) => tableAllowed(card.id)), targetValue).find(
      (choice) => choice.length > 1
    );

    if (topPlusSubset && (!tableSubset || topPlusSubset.subset.length + 1 <= tableSubset.length)) {
      const absorbed: Card[] = [];
      let table = state.tableLoose;
      topPlusSubset.subset.forEach((card) => {
        const removed = removeCardById(table, card.id);
        table = removed.next;
        absorbed.push(removed.removed);
      });
      state.tableLoose = table;

      const stack = state.collections[topPlusSubset.teamId];
      const top = getTopCard(stack.cards);
      if (top && top.id === topPlusSubset.topCardId && isNumeric(top)) {
        stack.cards.pop();
        absorbed.push(top);
      }

      if (absorbed.length > 0) {
        destination.push(...absorbed);
        pushLog(state, `${playerId} auto-absorbed: ${absorbed.map(fmtCard).join(', ')}.`);
        changed = true;
        continue;
      }
    }

    if (tableSubset) {
      const absorbed: Card[] = [];
      let table = state.tableLoose;
      tableSubset.forEach((card) => {
        const removed = removeCardById(table, card.id);
        table = removed.next;
        absorbed.push(removed.removed);
      });
      state.tableLoose = table;
      destination.push(...absorbed);
      pushLog(state, `${playerId} auto-absorbed: ${absorbed.map(fmtCard).join(', ')}.`);
      changed = true;
    }
  }
}

function refreshAbsorptionOrEnd(state: GameState, playerId: string): GameState {
  const pending = state.turnContext.pendingAbsorption;
  if (!pending) return endTurn(state, playerId);

  state.turnContext.pendingAbsorption = buildAbsorptionContext(state, playerId, pending.value, pending.destination);
  if (!state.turnContext.pendingAbsorption.choices.length) {
    state.turnContext.pendingAbsorption = null;
    return endTurn(state, playerId);
  }

  return state;
}

function buildAbsorptionContext(state: GameState, playerId: string, value: number, destination: Destination) {
  const player = requirePlayer(state, playerId);
  const subsetChoices = findSubsetChoices(state.tableLoose, value).map((subset, idx) => ({
    id: `table-${idx}`,
    type: 'tableSubset' as const,
    tableCardIds: subset.map((card) => card.id),
    collectionTopTeamId: null,
  }));

  const collectionChoices = Object.entries(state.collections)
    .filter(([teamId]) => teamId !== player.teamId)
    .map(([teamId, stack], idx) => ({ teamId, stack, idx }))
    .filter(({ stack }) => {
      const top = getTopCard(stack.cards);
      return top && isNumeric(top) && numericValue(top) === value;
    })
    .map(({ teamId, idx }) => ({
      id: `collection-${idx}`,
      type: 'collectionTop' as const,
      tableCardIds: [],
      collectionTopTeamId: teamId,
    }));

  return {
    playerId,
    value,
    destination,
    destinationCards: resolveDestination(state, destination).map((card) => card.id),
    choices: [...subsetChoices, ...collectionChoices],
  };
}

function resolveDestination(state: GameState, destination: Destination): Card[] {
  if (destination.zone === 'collection') {
    return state.collections[destination.teamId].cards;
  }

  if (destination.zone === 'build') {
    const pile = state.builds.find((entry) => entry.id === destination.pileId);
    if (!pile) throw new Error('Build destination not found.');
    return pile.cards;
  }

  if (destination.zone === 'stay') {
    if (destination.pileId) {
      const byId = state.stays.find((entry) => entry.id === destination.pileId);
      if (!byId) throw new Error('Stay destination not found.');
      return byId.cards;
    }

    const byOwnerValue = state.stays.find(
      (entry) => entry.ownerPlayerId === destination.ownerPlayerId && entry.value === destination.value
    );
    if (!byOwnerValue) throw new Error('Stay destination not found.');
    return byOwnerValue.cards;
  }

  throw new Error('Unknown destination zone.');
}

function endTurn(state: GameState, finishedPlayerId: string): GameState {
  if (state.phase !== PHASES.PLAYING) {
    return state;
  }

  state.turnContext.pendingAbsorption = null;
  state.turnContext.lastAction = undefined;

  const allHandsEmpty = Object.values(state.hands).every((cards) => cards.length === 0);
  if (allHandsEmpty) {
    return resolveRoundEnd(state, finishedPlayerId);
  }

  // Some moves can spend 2+ cards (e.g. certain royal actions), so hands may become uneven.
  // Never assign the turn to a player who has no legal turn actions (no hand cards and no pending royals).
  let nextId = nextPlayerId(state.players, finishedPlayerId);
  for (let i = 0; i < state.players.length; i += 1) {
    const hasHand = (state.hands[nextId] || []).length > 0;
    const hasPending = (state.royalPending[nextId] || []).length > 0;
    if (hasHand || hasPending) break;
    nextId = nextPlayerId(state.players, nextId);
  }
  state.currentPlayerId = nextId;
  state.turnContext.mustPlacePendingRoyal = (state.royalPending[state.currentPlayerId] || []).length > 0;
  return state;
}

function resolveRoundEnd(state: GameState, finishedPlayerId: string): GameState {
  const nextStarterId = nextPlayerId(state.players, finishedPlayerId);
  const isTwoPlayer = state.mode === MODES.TWO_PLAYER;
  const canStartNextRound = isTwoPlayer && state.meta.roundIndex === 0 && state.deck.length > 0;

  if (canStartNextRound) {
    state.phase = PHASES.BETWEEN_ROUNDS;
    state.meta.roundIndex = 1;
    state.currentPlayerId = nextStarterId;
    state.turnContext.mustPlacePendingRoyal = false;
    pushLog(state, 'Round ended. Ready for round 2.');
    return state;
  }

  sweepRemainingTableToLastCollector(state);

  state.phase = PHASES.SCORING;
  const scores = Object.fromEntries(
    Object.entries(state.collections).map(([teamId, stack]) => [teamId, computeScore(stack.cards)])
  );
  state.meta.scores = scores;

  state.phase = PHASES.GAME_OVER;
  pushLog(state, 'Game over. Scoring complete.');
  return state;
}

function requirePlayer(state: GameState, playerId: string): Player {
  const player = state.players.find((entry) => entry.id === playerId);
  if (!player) throw new Error(`Player ${playerId} not found.`);
  return player;
}

function countTeamHandValue(state: GameState, teamId: string, value: number): number {
  const teamPlayerIds = state.players.filter((entry) => entry.teamId === teamId).map((entry) => entry.id);
  return teamPlayerIds.reduce((sum, pid) => sum + countHandValue(state.hands[pid] || [], value), 0);
}

function pushLog(state: GameState, message: string): void {
  state.turnContext.log.push(message);
}

function markLastCollector(state: GameState, playerId: string): void {
  const player = requirePlayer(state, playerId);
  state.meta.lastCollectorPlayerId = playerId;
  state.meta.lastCollectorTeamId = player.teamId;
}

function sweepRemainingTableToLastCollector(state: GameState): void {
  const teamId = state.meta.lastCollectorTeamId;
  if (!teamId) return;

  const destination = state.collections[teamId]?.cards;
  if (!destination) return;

  const sweptCards: Card[] = [];
  sweptCards.push(...state.tableLoose);
  state.builds.forEach((build) => sweptCards.push(...build.cards));
  state.stays.forEach((stay) => sweptCards.push(...stay.cards));

  if (!sweptCards.length) return;

  destination.push(...sweptCards);
  state.tableLoose = [];
  state.builds = [];
  state.stays = [];

  const collector = state.meta.lastCollectorPlayerId || teamId;
  pushLog(state, `${collector} took remaining table cards (${sweptCards.length}).`);
}

function fmtCard(card: Card): string {
  const rank = card.rank === 'A' ? 1 : rankToValue(card.rank);
  return `${card.rank}${card.suit}(${rank})`;
}
